import { AdmPlan, UserId } from "@custom-types/base-types";
import { BaseProduct } from "@utils/product/product-utils-types";
import { allAvailableCustomProducts } from "@utils/product/custom-products";
import { allAvailableTrialProducts } from "@utils/product/trial-products";
import { allAvailableBundles } from "@utils/product/bundle-products";
import {
  isCreditProductIdentifier,
  isHoloBuilderLegacyPlanIdentifier,
} from "@utils/type-guards";
import {
  DEFAULT_USER_ID,
  isSceneSeatPlan,
  isTokenPlan,
} from "@utils/plan-utils";
import { ENDING_NOW_OFFSET_MS } from "@utils/plan-duration";

/** The subjects whose plans can be copied, i.e. the ones the "Add Subscription" page is offered for */
type CopyablePlanSubjectType = Extract<
  AdmPlan["subject"]["type"],
  "COMPANY" | "PROJECT"
>;

/**
 * The products a subscription can be copied as, i.e. the ones the "Add Subscription" page offers.
 * The token products are left out: they grant no bundle, so a plan cannot be told apart by them.
 */
export function getCopyableProducts({
  custom,
  trial,
  bundle,
}: {
  custom: BaseProduct[];
  trial: BaseProduct[];
  bundle: BaseProduct[];
}): BaseProduct[] {
  return [
    ...custom.filter(
      (product) => !isCreditProductIdentifier(product.identifier)
    ),
    ...trial,
    ...bundle.filter((product) =>
      isHoloBuilderLegacyPlanIdentifier(product.identifier)
    ),
  ];
}

/** The products a subscription can be copied as, known without fetching them from the backend */
const STATIC_COPYABLE_PRODUCTS = getCopyableProducts({
  custom: Object.values(allAvailableCustomProducts),
  trial: Object.values(allAvailableTrialProducts),
  bundle: Object.values(allAvailableBundles),
});

/** Whether the subject of a plan can receive a copy of it */
function isCopyablePlanSubjectType(
  subjectType: AdmPlan["subject"]["type"]
): subjectType is CopyablePlanSubjectType {
  return subjectType === "COMPANY" || subjectType === "PROJECT";
}

/**
 * Finds the product the plan was created from, by the bundles and features it grants.
 * A product only matches when it grants exactly the same ones and can be given to the subject of
 * the plan, as the copy is created from that product.
 *
 * @returns the product of the plan, or undefined when none of the products matches it
 */
export function findProductOfPlan(
  plan: AdmPlan,
  products: BaseProduct[] = STATIC_COPYABLE_PRODUCTS
): BaseProduct | undefined {
  const planIdentifiers = new Set<string>([
    ...plan.featureBundles.map(({ identifier }) => identifier),
    ...plan.features.map(({ identifier }) => identifier),
  ]);

  return products.find(
    (product) =>
      product.identifiers.length === planIdentifiers.size &&
      product.identifiers.every((identifier) =>
        planIdentifiers.has(identifier)
      ) &&
      product.scope[plan.subject.type].shouldShow
  );
}

/**
 * Whether the plan is a subscription that can be copied: it has to be created from one of the
 * products the "Add Subscription" page offers, and it needs an end date, as the copy starts from it.
 */
export function canCopyPlan(plan: AdmPlan): boolean {
  return (
    isCopyablePlanSubjectType(plan.subject.type) &&
    plan.status.identifier !== "deactivated" &&
    plan.endDate !== undefined &&
    !isSceneSeatPlan(plan) &&
    !isTokenPlan(plan) &&
    findProductOfPlan(plan) !== undefined
  );
}

/**
 * Whether the copied plan can be ended together with creating the copy. Only an active plan can:
 * an invalid one cannot be updated, an expired one would be extended to now instead, and one that
 * has not started yet would end before its start.
 */
export function canEndCopiedPlan(plan: AdmPlan): boolean {
  return plan.status.identifier === "active";
}

/**
 * The date a copy starts on unless the user picks another one. A plan that is not active anymore
 * leaves nothing to continue, so its copy starts today. An active plan is followed by its copy
 * when it ends on its own.
 *
 * @returns the start date of the copy, in milliseconds
 */
export function getDefaultCopyStartDate({
  copiedPlan,
  now = Date.now(),
}: {
  /** The plan that is copied */
  copiedPlan: AdmPlan;

  /** The current time, in milliseconds */
  now?: number;
}): number {
  if (!canEndCopiedPlan(copiedPlan) || !copiedPlan.endDate) {
    return now;
  }

  return Date.parse(copiedPlan.endDate);
}

/**
 * Whether the copy would run at the same time as the copied plan, so that the copied plan has to
 * be offered to end when the copy starts. Only an active plan can be ended, see canEndCopiedPlan.
 */
export function doesCopyOverlapCopiedPlan({
  copiedPlan,
  copyDates,
}: {
  /** The plan that is copied */
  copiedPlan: AdmPlan;

  /** The dates the copy runs between */
  copyDates: PlanDates;
}): boolean {
  if (!canEndCopiedPlan(copiedPlan) || !copiedPlan.endDate) {
    return false;
  }

  return (
    copyDates.startDate < Date.parse(copiedPlan.endDate) &&
    copyDates.endDate > Date.parse(copiedPlan.startDate)
  );
}

/** The dates a plan runs between, in milliseconds */
export interface PlanDates {
  /** When the plan starts */
  startDate: number;

  /** When the plan ends */
  endDate: number;
}

/**
 * The time the copied plan still has left: from now, or from its start when it has not started
 * yet, until its end. A copy using the remaining time runs exactly between these dates.
 *
 * @returns the remaining dates, or `null` when the plan has already run out
 */
export function getRemainingPlanDates({
  plan,
  now = Date.now(),
}: {
  /** The plan that is copied */
  plan: AdmPlan;

  /** The current time, in milliseconds */
  now?: number;
}): PlanDates | null {
  if (!plan.endDate) {
    return null;
  }

  const endDate = Date.parse(plan.endDate);
  const startDate = Math.max(now, Date.parse(plan.startDate));

  return endDate > startDate ? { startDate, endDate } : null;
}

/** The SCENE seats that are attached to the plan, whether they are assigned or not */
export function getAttachedSceneSeats(
  plans: AdmPlan[],
  planId: AdmPlan["id"]
): AdmPlan[] {
  return plans.filter(
    (plan) =>
      plan.metadata?.parentSubscriptionId === planId && isSceneSeatPlan(plan)
  );
}

/**
 * The members that hold one of the given SCENE seats. A deactivated seat is not held by anyone
 * anymore, and a member holding several seats is only listed once.
 *
 * @returns the IDs of the members, in the order of the seats they hold
 */
export function getSceneSeatAssignees(sceneSeats: AdmPlan[]): UserId[] {
  const assignees = sceneSeats
    .filter(
      (seat) =>
        seat.status.identifier !== "deactivated" &&
        seat.subject.id !== DEFAULT_USER_ID
    )
    .map((seat) => seat.subject.id);

  return [...new Set(assignees)];
}

/**
 * Whether the SCENE seats of a copied plan should move over to its copy right away.
 *
 * That is only the case when the copied plan stops granting SCENE access now: either it is ended
 * by a copy that starts right away, or it had already run out before. Otherwise the copied plan
 * keeps running until the copy starts, and its seats have to stay untouched until then, as the
 * seats of the copy only start together with it and the members would lose SCENE access early.
 */
export function shouldTransferSceneSeatsNow({
  copiedPlan,
  shouldEndCopiedPlan,
  copyStartDate,
  now = Date.now(),
}: {
  /** The plan that is copied */
  copiedPlan: AdmPlan;

  /** Whether the copied plan is ended together with creating the copy, i.e. when the copy starts */
  shouldEndCopiedPlan: boolean;

  /** When the copy starts, in milliseconds */
  copyStartDate: number;

  /** The current time, in milliseconds */
  now?: number;
}): boolean {
  // A copy that starts "now" starts a moment ahead, see getEndingNowDate
  if (shouldEndCopiedPlan && copyStartDate <= now + ENDING_NOW_OFFSET_MS) {
    return true;
  }

  return (
    copiedPlan.endDate !== undefined && Date.parse(copiedPlan.endDate) <= now
  );
}
